import { Download, GitCompare } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AuditTabs } from '@/components/audit/AuditTabs'
import { FilterBar, EMPTY_FILTERS, type Filters } from '@/components/audit/FilterBar'
import { RunSummary, auditButtonClass, useAuditRun } from '@/components/audit/RunDetail'
import { DataTable, type Column } from '@/components/data/DataTable'
import { SplitView } from '@/components/data/SplitView'
import { LoadingRows } from '@/components/data/SearchInput'
import { SectionBoundary } from '@/components/feedback/SectionBoundary'
import { Page } from '@/components/layout/Page'
import { api } from '@/lib/api'
import { buildAuditParams, durationMs, fmtDuration, fmtWhen, statusTone } from '@/lib/audit'
import { useSelection } from '@/lib/useSelection'

type AuditRow = {
  run_id: string
  actor_email: string
  surface: string
  operation: string
  status: string
  dry_run: boolean
  critical: boolean
  started_at: string
  finished_at: string | null
  atlassian_silent_failure: boolean
}

const PAGE = 50

function buildQuery(f: Filters, offset: number): string {
  const p = buildAuditParams(f)
  p.set('limit', String(PAGE))
  p.set('offset', String(offset))
  return p.toString()
}

function RunPane({ runId }: { runId: string }) {
  const d = useAuditRun(runId)
  if (d === undefined) return <LoadingRows label="Loading run" rows={6} />
  if (d === null) return <p className="px-4 py-3.5 text-[12.5px] text-text-2">Run not found.</p>
  return <RunSummary d={d} />
}

export default function AuditList() {
  const navigate = useNavigate()
  const [sel, setSel] = useSelection()
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
  const [debouncedQ, setDebouncedQ] = useState('')
  const [rows, setRows] = useState<AuditRow[] | null>(null)
  const [loadError, setLoadError] = useState(false)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [picked, setPicked] = useState<string[]>([])
  const reqId = useRef(0)

  // Selection for findings-diff: keep insertion order; toggling off removes the id.
  const togglePicked = (runId: string) =>
    setPicked((prev) => (prev.includes(runId) ? prev.filter((x) => x !== runId) : [...prev, runId]))

  // Debounce the free-text query so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q), 300)
    return () => clearTimeout(t)
  }, [filters.q])

  const effective = useMemo<Filters>(() => ({ ...filters, q: debouncedQ }), [filters, debouncedQ])

  function exportCsv() {
    const qs = buildAuditParams(effective).toString()
    window.open(`/api/audit/export.csv${qs ? `?${qs}` : ''}`, '_blank')
  }

  const fetchPage = useCallback(
    async (off: number, append: boolean) => {
      const id = ++reqId.current
      try {
        const page = await api<AuditRow[]>(`/audit?${buildQuery(effective, off)}`)
        if (id !== reqId.current) return // a newer request superseded this one
        const list = Array.isArray(page) ? page : []
        setRows((prev) => (append ? [...(prev ?? []), ...list] : list))
        setLoadError(!Array.isArray(page))
        setOffset(off)
        setHasMore(list.length === PAGE)
      } catch {
        if (id === reqId.current && !append) {
          setRows([])
          setLoadError(true)
        }
      }
    },
    [effective],
  )

  // Refetch page 0 whenever the effective filters change.
  useEffect(() => {
    fetchPage(0, false)
  }, [fetchPage])

  const columns: Column<AuditRow>[] = [
    {
      key: 'pick', header: 'Pick', width: '56px', title: () => undefined,
      render: (r) => (
        <input
          type="checkbox"
          checked={picked.includes(r.run_id)}
          onChange={() => togglePicked(r.run_id)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Pick ${r.operation} run for compare`}
          className="size-3.5 translate-y-[2px] accent-primary"
        />
      ),
    },
    {
      key: 'when', header: 'When', mono: true, width: '104px',
      render: (r) => fmtWhen(r.started_at), title: (r) => new Date(r.started_at).toLocaleString(),
    },
    {
      key: 'operation', header: 'Operation', mono: true,
      title: (r) => [r.operation, r.dry_run && 'dry run', r.critical && 'critical', r.atlassian_silent_failure && 'silent failure']
        .filter(Boolean).join(' · '),
      render: (r) => (
        <>
          {r.operation}
          {r.dry_run && <span className="ml-2 font-sans text-[11px] text-text-3">dry run</span>}
          {r.critical && <span className="ml-2 font-sans text-[11px] font-medium text-danger">critical</span>}
          {r.atlassian_silent_failure && <span className="ml-2 font-sans text-[11px] font-medium text-warn">silent failure</span>}
        </>
      ),
    },
    { key: 'actor_email', header: 'Who', width: '19%' },
    {
      key: 'status', header: 'Status', width: '112px',
      render: (r) => <span className={`font-medium ${statusTone(r.status)}`}>{r.status}</span>,
    },
    { key: 'surface', header: 'Surface', mono: true, width: '76px' },
    {
      key: 'ms', header: 'Duration', mono: true, width: '92px', align: 'right',
      render: (r) => fmtDuration(durationMs(r.started_at, r.finished_at)),
    },
  ]

  return (
    <Page
      title="Audit"
      hint="Every operation — who ran it, on which surface, and how it ended."
      context={<AuditTabs />}
      freshness={rows ? <span className="font-mono">{rows.length}{hasMore ? '+' : ''} runs</span> : undefined}
      actions={(
        <>
          {picked.length > 0 && (
            <>
              <span className="font-mono text-[11px] text-text-3">{picked.length} picked</span>
              <button
                type="button"
                disabled={picked.length !== 2}
                onClick={() => navigate(`/audit/compare?a=${picked[0]}&b=${picked[1]}`)}
                className={auditButtonClass}
                title={picked.length === 2 ? 'Compare the two runs’ findings' : 'Pick exactly 2 runs'}
              >
                <GitCompare aria-hidden className="size-3.5" /> Compare findings
              </button>
              <button
                type="button"
                onClick={() => setPicked([])}
                className="h-7 rounded-md px-2 text-[12px] text-text-2 hover:text-foreground"
              >
                Clear
              </button>
            </>
          )}
          <button type="button" onClick={exportCsv} className={auditButtonClass} title="Export the current filter as CSV">
            <Download aria-hidden className="size-3.5" /> Export CSV
          </button>
        </>
      )}
    >
      <FilterBar value={filters} onChange={setFilters} />
      <SplitView
        onCloseDetail={() => setSel(null)}
        emptyDetail="Select a run to see its details."
        detail={sel && (
          <SectionBoundary label="Run details" resetKeys={[sel]}>
            <RunPane runId={sel} />
          </SectionBoundary>
        )}
        list={rows === null
          ? <LoadingRows label="Loading runs" />
          : (
            <div className="flex min-h-0 flex-1 flex-col">
              {loadError && (
                <p role="alert" className="border-b border-line px-4 py-1.5 text-[12px] text-danger">
                  Couldn’t load the audit log.
                </p>
              )}
              <DataTable
                label="Audit runs"
                rows={rows}
                columns={columns}
                rowId={(r) => r.run_id}
                selectedId={sel}
                onSelect={setSel}
                empty="No runs match."
              />
              {hasMore && (
                <div className="flex shrink-0 justify-center border-t border-line py-2">
                  <button type="button" onClick={() => fetchPage(offset + PAGE, true)} className={auditButtonClass}>
                    Load more
                  </button>
                </div>
              )}
            </div>
          )}
      />
    </Page>
  )
}
